import { render, type TemplateResult } from 'lit-html';

/**
 * `PkOverlayBase` — shared a11y/overlay machinery for focus-trapped dialog-style
 * components (`pk-modal`, `pk-side-sheet`).
 *
 * Holds the common overlay lifecycle: shadow root, Escape key handling, the
 * focusin re-trap guard, connected/disconnected wiring, the `dismissible`
 * enum normalisation, open/close side-effect scheduling, inert application,
 * focus trap + return-focus, and the canonical close events (`pk-cancel`,
 * `pk-close`).
 *
 * Subclasses provide their own tag, styles, template, and any
 * component-specific behaviour, plus the small set of protected hooks below:
 *   - `template()`      — the lit-html template to render into the shadow root.
 *   - `_idPrefix`       — id prefix for generated element ids (e.g. `pk-modal`).
 *   - `_onAfterClose()` — optional; fires extra/legacy events after the close.
 */
export abstract class PkOverlayBase extends HTMLElement {
    protected readonly _root: ShadowRoot;
    protected readonly _keyHandler: (e: KeyboardEvent) => void;
    protected readonly _focusInHandler: (e: FocusEvent) => void;
    protected readonly _uid: string;
    private _titleIdValue: string | null = null;
    protected _previouslyFocused: Element | null = null;
    protected _inertedElements: Element[] = [];
    protected _openSideEffectsScheduled = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });

        this._uid = crypto.randomUUID().slice(0, 8);

        this._keyHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this._isEscapeDismissible()) {
                this._close();
            }
        };

        this._focusInHandler = (e: FocusEvent) => {
            // If open and focus lands outside the overlay, drag it back in.
            if (e.target instanceof Node && !this._root.contains(e.target) && !this.contains(e.target)) {
                e.preventDefault();
                this._trapFocus();
            }
        };
    }

    connectedCallback(): void {
        this._doRender();
        document.addEventListener('keydown', this._keyHandler);
        // If already open when connected, schedule side-effects via rAF.
        // Guard with _openSideEffectsScheduled to prevent double-scheduling when
        // attributeChangedCallback for 'open' also fires during the same parse.
        if (this.hasAttribute('open')) {
            this._scheduleOpenSideEffects();
        }
    }

    disconnectedCallback(): void {
        document.removeEventListener('keydown', this._keyHandler);
        document.removeEventListener('focusin', this._focusInHandler);
        this._removeInert();
    }

    attributeChangedCallback(_name: string, old: string | null, next: string | null): void {
        this._doRender();
        if (_name === 'open') {
            if (next !== null && old === null) {
                // opened
                this._scheduleOpenSideEffects();
            } else if (next === null && old !== null) {
                // closed
                this._onCloseSideEffects();
            }
        }
    }

    /** Programmatically open the overlay. */
    show(): void {
        this.setAttribute('open', '');
    }

    /** Programmatically close the overlay. */
    hide(): void {
        this._close();
    }

    // ── Dismissible logic ────────────────────────────────────────────────────

    /**
     * Normalise the `dismissible` attribute into the canonical enum value.
     * Back-compat: `"false"` -> `"none"`, absent/`"true"`/other -> `"backdrop"`.
     */
    protected _dismissibleValue(): 'none' | 'escape' | 'backdrop' {
        const val = this.getAttribute('dismissible');
        if (val === null || val === '' || val === 'true' || val === 'backdrop') return 'backdrop';
        if (val === 'false' || val === 'none') return 'none';
        if (val === 'escape') return 'escape';
        // Unknown value: default to backdrop.
        return 'backdrop';
    }

    protected _isDismissible(): boolean {
        return this._dismissibleValue() !== 'none';
    }

    protected _isEscapeDismissible(): boolean {
        const v = this._dismissibleValue();
        return v === 'escape' || v === 'backdrop';
    }

    protected _isBackdropDismissible(): boolean {
        return this._dismissibleValue() === 'backdrop';
    }

    // ── Lifecycle side-effects ───────────────────────────────────────────────

    protected _scheduleOpenSideEffects(): void {
        if (this._openSideEffectsScheduled) return;
        this._openSideEffectsScheduled = true;
        requestAnimationFrame(() => {
            this._openSideEffectsScheduled = false;
            if (this.hasAttribute('open')) {
                this._onOpenSideEffects();
            }
        });
    }

    protected _onOpenSideEffects(): void {
        // Capture focus before moving it.
        this._previouslyFocused = document.activeElement;

        // Apply inert to all siblings.
        this._applyInert();

        // Attach focusin guard.
        document.addEventListener('focusin', this._focusInHandler);

        this._trapFocus();

        this.dispatchEvent(new CustomEvent('pk-open', {
            bubbles: true,
            composed: true,
        }));
    }

    protected _onCloseSideEffects(): void {
        document.removeEventListener('focusin', this._focusInHandler);
        this._removeInert();
        this._returnFocus();
    }

    /**
     * The body child that contains this overlay — itself when it sits directly in <body>, otherwise the top-level
     * ancestor, walking up through shadow hosts (an overlay composed inside another component, e.g. pk-modal inside
     * pk-totp-prompt inside pk-totp-reprompt, or a modal rendered inside the page's <main>).
     */
    protected _topLevelAncestor(): Element {
        return PkOverlayBase._topOf(this);
    }

    private static _topOf(node: Element): Element {
        const root = node.getRootNode();
        const parent: Element | null = node.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
        if (!parent || parent === document.body || parent === document.documentElement) return node;
        return PkOverlayBase._topOf(parent);
    }

    protected _applyInert(): void {
        this._inertedElements = [];
        // Never inert the branch the overlay itself lives in — that would make the open dialog unusable.
        const own = this._topLevelAncestor();
        for (const child of Array.from(document.body.children)) {
            if (child === this || child === own) continue;
            if (!child.hasAttribute('inert')) {
                child.setAttribute('inert', '');
                this._inertedElements.push(child);
            }
        }
    }

    protected _removeInert(): void {
        for (const el of this._inertedElements) {
            el.removeAttribute('inert');
        }
        this._inertedElements = [];
    }

    protected _returnFocus(): void {
        const returnId = this.getAttribute('return-focus-to');
        if (returnId) {
            const target = document.getElementById(returnId);
            if (target) {
                (target as HTMLElement).focus();
                this._previouslyFocused = null;
                return;
            }
        }
        if (this._previouslyFocused && typeof (this._previouslyFocused as HTMLElement).focus === 'function') {
            (this._previouslyFocused as HTMLElement).focus();
        }
        this._previouslyFocused = null;
    }

    // ── Close ────────────────────────────────────────────────────────────────

    protected _close(): void {
        this.removeAttribute('open');

        this.dispatchEvent(new CustomEvent('pk-cancel', {
            bubbles: true,
            composed: true,
        }));
        this.dispatchEvent(new CustomEvent('pk-close', {
            bubbles: true,
            composed: true,
            detail: { reason: 'cancel' },
        }));

        this._onAfterClose();
    }

    /**
     * Hook for subclass-specific close events fired after the canonical
     * `pk-cancel`/`pk-close` pair (e.g. the legacy `pk-modal-close`).
     * Default: no-op.
     */
    protected _onAfterClose(): void {
        // no-op by default
    }

    protected _onBackdropClick(e: MouseEvent): void {
        // Only close if click landed directly on the backdrop, not the dialog.
        if (e.target === e.currentTarget && this._isBackdropDismissible()) {
            this._close();
        }
    }

    // ── Focus trap ───────────────────────────────────────────────────────────

    protected _trapFocus(): void {
        const focusable = this._focusableElements();
        if (focusable.length > 0) {
            (focusable[0] as HTMLElement).focus();
        }
    }

    protected _focusableElements(): Element[] {
        const selectors = [
            'button:not([disabled])',
            'a[href]',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(',');
        return Array.from(this._root.querySelectorAll(selectors));
    }

    protected _handleTabKey(e: KeyboardEvent): void {
        if (e.key !== 'Tab') return;
        const focusable = this._focusableElements() as HTMLElement[];
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last  = focusable.at(-1);
        if (!first || !last) return;

        if (e.shiftKey) {
            if (this._root.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else if (this._root.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    // ── Render ───────────────────────────────────────────────────────────────

    /**
     * Id for the title heading element, referenced by `aria-labelledby`.
     * Lazily derived from the subclass `_idPrefix` (which is not available in
     * the base constructor) and the per-instance `_uid`.
     */
    protected get _titleId(): string {
        if (this._titleIdValue === null) {
            this._titleIdValue = `${this._idPrefix}-title-${this._uid}`;
        }
        return this._titleIdValue;
    }

    protected _doRender(): void {
        render(this.template(), this._root);
    }

    // ── Subclass hooks ───────────────────────────────────────────────────────

    /** Id prefix for generated element ids, e.g. `pk-modal` or `pk-side-sheet`. */
    protected abstract get _idPrefix(): string;

    /** The lit-html template rendered into the shadow root. */
    protected abstract template(): TemplateResult;
}
